"use client"

import {
  PieChart as RPieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Label, Customized,
} from "recharts"
import {
  type BaseChartProps, DEFAULT_PALETTE, colorFor, type ChartDatum,
} from "./types"
import type { ReactNode } from "react"

export interface PieChartProps extends Omit<BaseChartProps, "series"> {
  /** Field that holds the slice value. Defaults to "value". */
  valueKey?: string
  /** Field that holds the slice label. Defaults to "name". */
  nameKey?: string
  /** Slice label rendering — false hides them. */
  showSliceLabels?: boolean
  /** Render as donut (hollow centre). */
  donut?: boolean
  /** Inner radius (donut hole). */
  innerRadius?: number | string
  /** Outer radius (defaults to "80%"). */
  outerRadius?: number | string
  /** Pie sweep — default 360 (full circle); use 180 for half-circle gauges. */
  sweepDeg?: number
  /** Optional centre-text overlay (e.g. total). */
  centerLabel?: string
  /** Gap between slices (in degrees). */
  paddingAngle?: number
  /** Round slice corners by N pixels. */
  cornerRadius?: number
  /** Render slice fills with a vertical gradient. */
  gradient?: boolean
  /** Optional second-level pie inside the first (two-level pie). */
  innerPie?: {
    data: ChartDatum[]
    valueKey?: string
    nameKey?: string
    palette?: string[]
  }
  /** Show an arrow needle pointing at a value (gauge style). */
  needleValue?: number
  /** Maximum value used to compute the needle angle (defaults to sum). */
  needleMax?: number
  /** Custom slice label renderer. */
  labelRenderer?: (entry: { name: string; value: number; percent: number; index: number }) => ReactNode
}

export function PieChart({
  data, valueKey = "value", nameKey = "name", xKey,
  palette = DEFAULT_PALETTE, colorMap,
  showLegend = true, showTooltip = true,
  className, style, height = "100%", width = "100%",
  donut, innerRadius, outerRadius = "80%", sweepDeg, centerLabel, showSliceLabels = true, compact,
  paddingAngle, cornerRadius, gradient, innerPie, needleValue, needleMax, labelRenderer,
}: PieChartProps) {
  const valueField = valueKey
  const nameField = xKey || nameKey
  const startAngle = sweepDeg && sweepDeg < 360 ? 180 + (180 - sweepDeg) / 2 : 90
  const endAngle = sweepDeg && sweepDeg < 360 ? startAngle - sweepDeg : -270
  const isDonut = !!donut
  const innerR = innerRadius ?? (isDonut ? "55%" : 0)

  // Needle calculation — total of values forms the dial range when caller
  // didn't pass `needleMax` explicitly.
  const total = data.reduce((s, d) => s + (Number(d[valueField]) || 0), 0)
  const dialMax = needleMax ?? total
  const needleAngle = needleValue !== undefined && dialMax > 0
    ? startAngle - ((needleValue / dialMax) * (startAngle - endAngle))
    : null

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RPieChart>
        <defs>
          {gradient && data.map((d, i) => {
            const c = colorFor(String(d[nameField] ?? i), i, palette, colorMap)
            return (
              <linearGradient key={i} id={`pie-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={1} />
                <stop offset="100%" stopColor={c} stopOpacity={0.5} />
              </linearGradient>
            )
          })}
        </defs>
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {/* Outer (or only) ring */}
        <Pie
          data={data}
          dataKey={valueField}
          nameKey={nameField}
          cx="50%" cy="50%"
          startAngle={startAngle} endAngle={endAngle}
          outerRadius={outerRadius}
          innerRadius={innerR}
          paddingAngle={paddingAngle}
          cornerRadius={cornerRadius}
          label={showSliceLabels ? (labelRenderer as never) ?? { fontSize: 10.5 } : false}
          isAnimationActive={false}
        >
          {data.map((d: ChartDatum, i: number) => (
            <Cell key={i} fill={gradient ? `url(#pie-grad-${i})` : colorFor(String(d[nameField] ?? i), i, palette, colorMap)} />
          ))}
          {centerLabel && (
            <Label value={centerLabel} position="center" style={{ fontSize: 14, fontWeight: 600, fill: "currentColor" }} />
          )}
        </Pie>
        {/* Inner ring (two-level pie) */}
        {innerPie && (
          <Pie
            data={innerPie.data}
            dataKey={innerPie.valueKey || "value"}
            nameKey={innerPie.nameKey || "name"}
            cx="50%" cy="50%"
            startAngle={startAngle} endAngle={endAngle}
            outerRadius={typeof innerR === "string" ? `${parseInt(innerR) - 5}%` : Math.max(0, Number(innerR) - 6)}
            innerRadius={0}
            isAnimationActive={false}
          >
            {innerPie.data.map((d, i) => (
              <Cell key={i} fill={(innerPie.palette || palette)[i % (innerPie.palette || palette).length]} />
            ))}
          </Pie>
        )}
        {/* Needle for gauge-style pies — drawn in SVG coords via Customized,
         *  which gives us the resolved chart geometry at render time. */}
        {needleAngle !== null && (
          <Customized component={(props: { width?: number; height?: number }) => {
            const w = props.width ?? 0, h = props.height ?? 0
            if (!w || !h) return null
            const cx = w / 2, cy = h / 2
            const r = Math.min(w, h) * 0.4
            const rad = (needleAngle * Math.PI) / 180
            const x = cx + r * Math.cos(rad)
            const y = cy - r * Math.sin(rad)
            return (
              <g>
                <line x1={cx} y1={cy} x2={x} y2={y} stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={4} fill="currentColor" />
              </g>
            )
          }} />
        )}
      </RPieChart>
    </ResponsiveContainer>
  )
}

/** Donut-shape preset — equivalent to PieChart with `donut` set. */
export function DonutChart(props: PieChartProps) {
  return <PieChart {...props} donut />
}

/** Half-circle gauge built with PieChart's `sweepDeg` + needle. */
export interface NeedleGaugeChartProps extends Omit<PieChartProps, "sweepDeg"> {
  value: number
  max?: number
}
export function NeedleGaugeChart({ value, max, ...rest }: NeedleGaugeChartProps) {
  return <PieChart {...rest} sweepDeg={180} donut needleValue={value} needleMax={max} />
}
